import React, { useMemo, useState } from "react";
import {
  DialogContainer,
  Dialog,
  Content,
  ComboBox,
  Item,
  Divider,
  ListBox,
  View,
  Flex,
  Heading,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import { IndividualCannedResponses } from "../../../types/agent.type";

type CannedResponseModalProps = {
  setOpenCannedModal: (value: boolean) => void;
  setCannedResponse: (value: string) => void;
};

const CannedResponseModal: React.FC<CannedResponseModalProps> = ({
  setOpenCannedModal,
  setCannedResponse,
}) => {
  const cannedResponses = useSelector(
    (state: RootState) => state.agent.cannedResponses[0]?.responses || []
  );

  const [searchValue, setSearchValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Memoize categories as objects
  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(
        cannedResponses.map((res: IndividualCannedResponses) => res.category)
      )
    );
    return unique.map((cat) => ({ id: cat, name: cat }));
  }, [cannedResponses]);

  const filteredResponses = useMemo(() => {
    if (!selectedCategory) return [];
    return cannedResponses.filter(
      (res: IndividualCannedResponses) => res.category === selectedCategory
    );
  }, [selectedCategory, cannedResponses]);

  const handleSelection = (key: React.Key) => {
    const selected = cannedResponses.find(
      (res: IndividualCannedResponses) => res.id === key
    );
    if (selected) {
      setCannedResponse(selected.content);
      closeModal();
    }
  };

  const closeModal = () => setOpenCannedModal(false);

  return (
    <DialogContainer onDismiss={closeModal}>
      <Dialog width="size-5000">
        <Heading level={4}>
          <Flex
            UNSAFE_className="statusDialogHeading"
            justifyContent="space-between"
          >
            <span className="dialogHeading">Select a response</span>
            <span className="closeDialogButton" onClick={closeModal}>
              x
            </span>
          </Flex>
        </Heading>

        <Divider />

        <Content>
          <ComboBox
            data-testid="canned-response-modal-combobox"
            aria-label="canned-response-modal"
            label="Search and select"
            placeholder="Search by category..."
            inputValue={searchValue}
            width="250px"
            onInputChange={setSearchValue}
            selectedKey={selectedCategory || undefined}
            onSelectionChange={(key) => {
              const category = key as string;
              setSelectedCategory(category);
              setSearchValue(category);
              setSelectedKey(null);
            }}
            allowsCustomValue={false}
            menuTrigger="input"
            items={(categories as { id: string; name: string }[]).filter(
              (item) =>
                item.name.toLowerCase().includes(searchValue.toLowerCase())
            )}
          >
            {(item: any) => <Item key={item.id}>{item.name}</Item>}
          </ComboBox>

          <View
            marginTop="size-200"
            height="size-1600"
            overflow="auto"
            borderTopWidth="thin"
            borderColor="gray-300"
            paddingTop="size-150"
          >
            <ListBox
              data-testid="canned-response-modal-listbox"
              aria-label="All canned responses"
              selectionMode="single"
              selectedKeys={selectedKey ? [selectedKey] : []}
              onSelectionChange={(keys) => {
                const [key] = Array.from(keys);
                setSelectedKey(key as string);
                handleSelection(key);
              }}
              items={filteredResponses}
            >
              {(item: IndividualCannedResponses) => (
                <Item key={item.id}>{item.title}</Item>
              )}
            </ListBox>
          </View>
        </Content>
      </Dialog>
    </DialogContainer>
  );
};

export default CannedResponseModal;
